import { useEffect, useState, useMemo } from 'react';
import {
  useShipToLocationsState,
  useShipToLocationsActions,
  useSelectedShipTo,
} from '../../../hooks/useShipToLocations';
import { useShelterState } from '../../../hooks/useShelter';
import { Dropdown } from '@hills-design-system/components';
import { useTranslation } from '../../../hooks/useTranslation';
import { TranslationKeyEnum } from '../../../types/constants';

interface ShipToFilterProps {
  onShipToChange: (shipTo: string | null) => void;
}

interface ShipToOption {
  id: string;
  label: string;
  description?: string;
}

export const ShipToFilter: React.FC<ShipToFilterProps> = ({ onShipToChange }) => {
  const shipToLocations = useShipToLocationsState();
  const selectedShipTo = useSelectedShipTo();
  const { getShipToLocations, selectShipTo } = useShipToLocationsActions();
  const { shelter } = useShelterState();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setIsLoading(true);
        setError(null);
        await getShipToLocations();
      } catch (err) {
        setError(err instanceof Error ? err.message : t(TranslationKeyEnum.FailedToLoadLocations));
      } finally {
        setIsLoading(false);
      }
    };
    fetchLocations();
  }, [getShipToLocations]);

  // Auto-select preferred ship-to after locations are loaded and shelter is available
  useEffect(() => {
    if (shelter?.preferred_ship_to && shipToLocations?.locations) {
      const preferredExists = shipToLocations.locations.some(
        (location) => location.shipTo === shelter.preferred_ship_to
      );
      if (preferredExists) {
        selectShipTo(shelter.preferred_ship_to);
      }
    }
  }, [shelter?.preferred_ship_to, shipToLocations?.locations, selectShipTo]);

  useEffect(() => {
    onShipToChange(selectedShipTo);
  }, [selectedShipTo, onShipToChange]);

  const locations = useMemo(() => shipToLocations?.locations ?? [], [shipToLocations?.locations]);

  const options = useMemo(() => {
    // Separate the default location from other locations
    const defaultLocation = locations.find((loc) => loc.shipTo === shelter?.preferred_ship_to);
    const otherLocations = locations.filter((loc) => loc.shipTo !== shelter?.preferred_ship_to);

    // Sort other locations alphabetically by name
    const sortedOtherLocations = [...otherLocations].sort((a, b) => a.name.localeCompare(b.name));

    // Build options array with specific ordering
    const sortedOptions: ShipToOption[] = [
      {
        id: 'all',
        label: t(TranslationKeyEnum.AllLocations),
      },
    ];

    // Add default location as position 2 if it exists
    if (defaultLocation) {
      sortedOptions.push({
        id: defaultLocation.shipTo,
        label: `${defaultLocation.shipTo} - ${defaultLocation.name} ${t(TranslationKeyEnum.DefaultLocation)}`,
        description: defaultLocation.address,
      });
    }

    // Add remaining locations in alphabetical order
    sortedOtherLocations.forEach((location) => {
      sortedOptions.push({
        id: location.shipTo,
        label: `${location.shipTo} - ${location.name}`,
        description: location.address,
      });
    });

    return sortedOptions;
  }, [locations, shelter?.preferred_ship_to]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 bg-gray-100 border border-gray-300 rounded-lg animate-pulse">
        <div className="h-4 bg-gray-300 rounded w-32" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-300 rounded-lg">
        <span className="text-detail-90 font-medium text-red-700">{error}</span>
      </div>
    );
  }

  if (locations.length === 0) {
    return null;
  }

  if (locations.length === 1) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 bg-white border border-gray-300 rounded-lg">
        <span className="text-detail-90 font-medium text-black-950">
          {`${locations[0].shipTo} - ${locations[0].name}`}
        </span>
      </div>
    );
  }

  const handleChange = (id: string) => {
    const newShipTo = id === 'all' ? null : id;
    selectShipTo(newShipTo);
  };

  return (
    <div className="w-full">
      <Dropdown
        label=""
        placeholder={t(TranslationKeyEnum.SelectALocation)}
        options={options}
        selectedId={selectedShipTo ?? 'all'}
        onChange={handleChange}
      />
    </div>
  );
};
